import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';

import { WasmService } from '../../../../core/service/wasm.service';
import { KeyStatus } from '../../../../models/types';
import { CardType } from '../card/card';

type UnlockState = 'idle' | 'unlocking' | 'unlocked' | 'error';

@Component({
  selector: 'app-lock-card',
  standalone: true,
  imports: [CommonModule],
  animations: [
    trigger('iconAnim', [
      transition(':enter', [
        style({ transform: 'scale(0.8)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'scale(0.8)', opacity: 0 })),
      ]),
    ]),
    trigger('iconOpenAnim', [
      transition(':enter', [
        style({ transform: 'scale(0) rotate(-20deg)' }),
        animate(
          '400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          style({ transform: 'scale(1) rotate(0)' }),
        ),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'scale(0)' }))]),
    ]),
    trigger('textFade', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(3px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  template: `
    <div
      class="relative overflow-hidden rounded-2xl border transition-colors duration-300"
      [style.background-color]="cardBg()"
      [style.border-color]="cardBorder()"
    >
      <div class="relative z-10 p-4">
        <div class="flex items-center gap-3">
          <!-- ── Icon ── -->
          <div class="relative shrink-0">
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl transition-colors duration-300"
              [style.background-color]="iconBg()"
              [class.animate-pulse-slow]="isUnlocking()"
            >
              @if (isUnlocked()) {
                <div @iconOpenAnim class="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    [style.color]="iconColor()"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
                  </svg>
                </div>
              } @else if (isUnlocking()) {
                <div @iconAnim class="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    [style.color]="iconColor()"
                  >
                    <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"></path>
                    <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"></path>
                    <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"></path>
                    <path d="M16.37 2a20.16 20.16 0 0 1 0 20"></path>
                  </svg>
                </div>
              } @else {
                <div @iconAnim class="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    [style.color]="iconColor()"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
              }
            </div>
          </div>

          <!-- ── Info ── -->
          <div class="min-w-0 flex-1">
            <h3 class="truncate font-semibold" style="color: var(--th-device-name)">
              {{ card().device_name }}
            </h3>
            <p class="mt-0.5 text-sm opacity-80" style="color: var(--th-label)">
              {{ card().bluetooth_id }}
            </p>
            <div class="mt-1 h-4 overflow-hidden">
              <p
                [@textFade]="unlockState()"
                class="text-xs font-medium tracking-wide"
                [style.color]="subText().color"
              >
                {{ subText().text }}
              </p>
            </div>
          </div>

          <!-- ── Unlock button ── -->
          <button
            (click)="handleUnlock()"
            [disabled]="
              isUnlocking() || isUnlocked() || (anyUnlocking() && !isUnlocking()) || isDisabled()
            "
            class="flex shrink-0 items-center justify-center rounded-lg px-4 py-2 transition-all duration-200 active:scale-90 disabled:cursor-default"
            [style.background-color]="btnBg()"
            [style.color]="btnColor()"
          >
            @if (isUnlocking()) {
              <div
                class="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                style="border-color: var(--th-loading); border-top-color: transparent"
              ></div>
            } @else {
              Unlock
            }
          </button>
        </div>

        <!-- ── Progress Bar ── -->
        @if (isUnlocking()) {
          <div
            class="mt-4 border-t pt-3"
            style="border-color: color-mix(in srgb, var(--th-label) 10%, transparent)"
          >
            <div class="mb-2 flex items-center justify-between">
              <span class="text-xs font-medium" style="color: var(--th-label)">
                {{ progressMessage() || 'Initializing...' }}
              </span>
              <span class="font-mono text-xs font-bold" style="color: var(--th-loading)">
                {{ progressPercentage() }}%
              </span>
            </div>
            <div class="flex gap-1">
              @for (segment of [].constructor(progressTotal()); track $index) {
                <div
                  class="h-1.5 flex-1 rounded-full transition-colors duration-200"
                  [style.background-color]="getSegmentColor($index + 1)"
                  [class.animate-pulse]="isUnlocking() && $index + 1 === progressStep()"
                ></div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .animate-pulse-slow {
        animation: pulse-scale 1.5s ease-in-out infinite;
      }
      @keyframes pulse-scale {
        0%,
        100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.06);
        }
      }
    `,
  ],
})
export class LockCardComponent implements OnDestroy {
  wasmService = inject(WasmService);

  card = input.required<CardType>();
  unlockFn = input.required<(card: CardType) => Promise<'success' | 'error' | 'cancelled'>>();
  anyUnlocking = input<boolean>(false);
  keyStatus = input<KeyStatus>('none');
  isDisabled = input<boolean>(false);

  unlockState = signal<UnlockState>('idle');
  errorMsg = signal<string>('');

  private timeoutIds: ReturnType<typeof setTimeout>[] = [];

  isUnlocked = computed(() => this.unlockState() === 'unlocked');
  isUnlocking = computed(() => this.unlockState() === 'unlocking');
  isError = computed(() => this.unlockState() === 'error');

  // Wasm progress integration
  progressMessage = computed(() => (this.isUnlocking() ? this.wasmService.progressMessage() : ''));
  progressStep = computed(() => (this.isUnlocking() ? this.wasmService.progressStep() : 0));
  progressTotal = computed(() =>
    this.isUnlocking() ? Math.max(1, this.wasmService.progressTotal()) : 0,
  );

  progressPercentage = computed(() => {
    const total = this.progressTotal();
    const step = this.progressStep();
    if (total === 0) return 0;
    return Math.round((step / total) * 100);
  });

  getSegmentColor(segmentStep: number): string {
    const currentStep = this.progressStep();
    if (segmentStep < currentStep) return 'var(--th-loading)';
    if (segmentStep === currentStep)
      return 'color-mix(in srgb, var(--th-loading) 60%, transparent)';
    return 'color-mix(in srgb, var(--th-label) 15%, transparent)';
  }

  canUnlock = computed(() => {
    const start = new Date(this.card().start_date).getTime();
    const end = new Date(this.card().end_date).getTime();
    const now = Date.now();
    return now >= start && now <= end && !this.card().isDisabled && this.keyStatus() !== 'expired';
  });

  // ── Derived colors ──
  cardBg = computed(() => {
    if (this.isUnlocked()) return 'color-mix(in srgb, var(--th-success) 12%, var(--th-card-bg))';
    if (this.isError()) return 'color-mix(in srgb, var(--th-error) 10%, var(--th-card-bg))';
    return 'var(--th-card-bg)';
  });

  cardBorder = computed(() => {
    if (this.isUnlocked()) return 'color-mix(in srgb, var(--th-success) 30%, transparent)';
    if (this.isError()) return 'color-mix(in srgb, var(--th-error) 35%, transparent)';
    return 'color-mix(in srgb, var(--th-label) 15%, transparent)';
  });

  iconBg = computed(() => {
    if (this.isUnlocked()) return 'color-mix(in srgb, var(--th-success) 15%, transparent)';
    if (this.isUnlocking()) return 'color-mix(in srgb, var(--th-loading) 15%, transparent)';
    if (this.isError()) return 'color-mix(in srgb, var(--th-error) 15%, transparent)';
    return 'color-mix(in srgb, var(--th-label) 10%, transparent)';
  });

  iconColor = computed(() => {
    if (this.isUnlocked()) return 'var(--th-success-text)';
    if (this.isUnlocking()) return 'var(--th-loading)';
    if (this.isError()) return 'var(--th-error)';
    return this.canUnlock()
      ? 'var(--th-device-name)'
      : 'color-mix(in srgb, var(--th-label) 40%, transparent)';
  });

  statusDotColor = computed(() => {
    if (this.isUnlocked()) return 'var(--th-success)';
    if (this.canUnlock()) return 'var(--th-loading)';
    return 'color-mix(in srgb, var(--th-label) 30%, transparent)';
  });

  subText = computed(() => {
    if (this.isUnlocked())
      return { text: 'Unlocked successfully', color: 'var(--th-success-text)' };
    if (this.isError()) return { text: this.errorMsg(), color: 'var(--th-error)' };
    if (this.isUnlocking()) return { text: 'Sending signal…', color: 'var(--th-loading)' };
    return {
      text: this.canUnlock() ? 'Stand near the lock' : '-',
      color: 'color-mix(in srgb, var(--th-device-name) 80%, transparent)',
    };
  });

  btnBg = computed(() => {
    if (this.isUnlocked()) return 'color-mix(in srgb, var(--th-success) 15%, transparent)';
    if (this.isUnlocking()) return 'color-mix(in srgb, var(--th-loading) 15%, transparent)';
    if (this.anyUnlocking() || !this.canUnlock() || this.isDisabled())
      return 'color-mix(in srgb, var(--th-label) 8%, transparent)';
    return 'var(--th-card-btn)';
  });

  btnColor = computed(() => {
    if (this.isUnlocked()) return 'var(--th-success-text)';
    if (this.isUnlocking()) return 'var(--th-loading)';
    if (this.anyUnlocking() || !this.canUnlock() || this.isDisabled())
      return 'color-mix(in srgb, var(--th-label) 35%, transparent)';
    return 'var(--th-bg)';
  });

  constructor() {
    effect(() => {
      const state = this.unlockState();

      this.clearTimeouts();

      if (state === 'unlocked') {
        const t = setTimeout(() => this.unlockState.set('idle'), 4000);
        this.timeoutIds.push(t);
      } else if (state === 'error') {
        const t = setTimeout(() => {
          this.unlockState.set('idle');
          this.errorMsg.set('');
        }, 3000);
        this.timeoutIds.push(t);
      }
    });
  }

  async handleUnlock(): Promise<void> {
    if (this.unlockState() !== 'idle') return;

    if (!this.canUnlock()) {
      this.errorMsg.set('Key required. Connect to the internet.');
      this.unlockState.set('error');
      return;
    }

    this.unlockState.set('unlocking');

    try {
      const result = await this.unlockFn()(this.card());
      if (result === 'success') {
        this.unlockState.set('unlocked');
      } else if (result === 'error') {
        this.errorMsg.set('Unlock failed.');
        this.unlockState.set('error');
      } else {
        // 'cancelled' — user dismissed the picker; reset silently
        this.unlockState.set('idle');
      }
    } catch {
      this.unlockState.set('error');
      this.errorMsg.set('Unlock failed — try again');
    }
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
  }

  private clearTimeouts(): void {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds = [];
  }
}
